import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme } from '@material-ui/core/styles';

import Button from '../../../../components/Button/Button';
import CollapsedSection from '../DetailsSection';
import { dataAttributesState } from '../helper';
import { getIconColour } from '../../../../helpers/app';
import FileUnknownIcon from '../../../../assets/icons/file_unknown.svg';
import FileIcon from '../../../../assets/icons/file.svg';
import UploadIcon from '../../../../assets/icons/upload.svg';
import CompleteIcon from '../../../../assets/icons/complete.svg';
import ServerIcon from '../../../../assets/icons/server';
import { ExpandContent, SectionRow, UploadRow, UploadCard } from '../styles';

type PropsType = {
  expand?: boolean;
  onClick: () => void;
  onDownload: () => void;
  onSubmit: (file: File) => Promise<boolean | undefined>;
  hasPermissionsForm?: boolean;
};

type ExpandPropsType = {
  onDownload: () => void;
  onSubmit: (file: File) => Promise<boolean | undefined>;
  hasPermissionsForm?: boolean;
};

const FILE_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

const NoFile: React.FC<{ getInputProps: any }> = ({ getInputProps }) => (
  <>
    <div className="ds-data-attribute-title file text-subtitle">
      Drag & drop or select a file to upload.
    </div>

    <div>
      <input {...getInputProps()} id="ds-section-stage-2" />
      <img src={FileUnknownIcon} alt="No File" />
    </div>

    <Button text="Select" className="ds-app-button disabled-opacity" type="button" />
  </>
);

const UploadReady: React.FC<{
  getInputProps: any;
  onSubmit: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}> = ({ getInputProps, onSubmit }) => (
  <>
    <div className="ds-data-attribute-title upload text-subtitle">Ready to upload?</div>

    <div>
      <input {...getInputProps()} id="ds-section-stage-2" />
      <img src={UploadIcon} alt="Upload" />
    </div>

    <Button text="Submit" className="ds-app-button" type="button" onClick={onSubmit} />
  </>
);

const FileUploaded: React.FC<{ getInputProps: any; fileName: string }> = ({
  getInputProps,
  fileName,
}) => (
  <>
    <div className="ds-data-attribute-title success text-subtitle">Upload complete!</div>

    <div>
      <input {...getInputProps()} id="ds-section-stage-2" />
      <img src={CompleteIcon} alt="Complete" />
      <div className="test-small">{fileName}</div>
    </div>

    <div className="text-small-bold">Upload a different file</div>
  </>
);

const ExpandedDataAttributes: React.FC<ExpandPropsType> = ({
  onDownload,
  onSubmit,
  hasPermissionsForm,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFile, setUploadedFile] = useState('');
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: FILE_ACCEPT,
  });

  const onFileSubmit = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    if (!files[0]) return;

    const success = await onSubmit(files[0]);
    if (success) {
      setUploadedFile(files[0].name);
      setFiles([]);
    }
  };

  const StageTwoComponent = () => {
    if (files[0]) return <UploadReady getInputProps={getInputProps} onSubmit={onFileSubmit} />;

    if (hasPermissionsForm || uploadedFile)
      return <FileUploaded getInputProps={getInputProps} fileName={uploadedFile} />;

    return <NoFile getInputProps={getInputProps} />;
  };

  return (
    <ExpandContent>
      <SectionRow>
        <div className="text-small">
          This section requires you to provide information about the data attributes your
          application will be collecting and storing, along with each field’s purpose and
          permissions.
        </div>
      </SectionRow>

      <UploadRow>
        <div className="ds-section-data-wrapper">
          <label className="text-small" htmlFor="ds-section-stage-1">
            Stage 1
          </label>
          <UploadCard>
            <div className="ds-section-data-title" id="ds-section-stage-1">
              Download & Complete Spreadsheet
            </div>

            <img src={FileIcon} alt="Download" />

            <Button text="Download" className="ds-app-button" onClick={onDownload} type="button" />
          </UploadCard>
        </div>

        <div className="ds-section-data-wrapper">
          <label className="text-small" htmlFor="ds-section-stage-2">
            Stage 2
          </label>
          <UploadCard {...getRootProps({ className: 'dashed' })}>{StageTwoComponent()}</UploadCard>
        </div>
      </UploadRow>
    </ExpandContent>
  );
};

const DataAttributes: React.FC<PropsType> = ({
  expand,
  onClick,
  onDownload,
  onSubmit,
  hasPermissionsForm = false,
}) => {
  const type = dataAttributesState(hasPermissionsForm);
  const theme = useTheme();

  return (
    <CollapsedSection
      icon={<ServerIcon stroke={getIconColour(type, theme)} />}
      title="Data Attributes"
      description="Cross-matched data fields naming, purpose, and permissions."
      expand={expand}
      onClick={onClick}
      type={type}
    >
      <ExpandedDataAttributes
        onDownload={onDownload}
        onSubmit={onSubmit}
        hasPermissionsForm={hasPermissionsForm}
      />
    </CollapsedSection>
  );
};

export default DataAttributes;
